import { Injectable, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Observable, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  Account, Group, Category, Rule, Txn, ImportRecord, Budget, RecurringState, RecurringStatus,
  BalanceIndex, moneyBase, SEED_GROUPS, SEED_CATEGORIES, SEED_TRANSFER_RULES,
  SavingsGoal, Projection, DEFAULT_PROJECTION,
  MortgageSettings, DEFAULT_MORTGAGE, FireSettings, DEFAULT_FIRE,
} from './money.model';
import { CommitChunk } from './csv/commit-payload';
import { hash64 } from './csv/parse-values';

export interface MoneyData {
  txns: Txn[];
  accounts: Account[];
  groups: Group[];
  categories: Category[];
  rules: Rule[];
  imports: ImportRecord[];
  /** Set once, when the seed taxonomy is written. Null means never seeded. */
  seededAt: number | null;
  budgets: Record<string, Budget>;
  recurringState: Record<string, RecurringState>;
  /** Closing balances by account and date. Empty for a source that reports none. */
  balances: BalanceIndex;
}

export interface SavingsData { goals: SavingsGoal[]; projection: Projection; }

/** The FIRE screen cannot compute a target without the loan, so both nodes
 *  arrive together. */
export interface FireData { fire: FireSettings; mortgage: MortgageSettings; }

/**
 * Multi-path patch for a budget. `null` deletes — Firebase throws on undefined,
 * and null is how a value is removed.
 */
export function budgetPatch(
  categoryId: string, amount: number | null, month?: string,
): Record<string, unknown> {
  if (month) {
    return { [`budgets/${categoryId}/overrides/${month}`]: amount };
  }
  return amount === null
    ? { [`budgets/${categoryId}`]: null }
    : { [`budgets/${categoryId}/amount`]: amount };
}

/**
 * Stored settings over their defaults, treating a null child as absent.
 *
 * A plain spread is not enough: RTDB reports a removed child as null, and
 * null is not undefined, so `{ ...defaults, ...stored }` would keep it. Every
 * field in these nodes feeds arithmetic, where a surviving null becomes 0 or
 * NaN in a dollar figure on screen instead of failing loudly.
 */
export function withDefaults<T extends object>(defaults: T, stored: Partial<T> | null | undefined): T {
  const out = { ...defaults };
  Object.entries(stored ?? {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined) (out as Record<string, unknown>)[key] = value;
  });
  return out;
}

@Injectable({ providedIn: 'root' })
export class MoneyService {
  private injector = inject(EnvironmentInjector);

  constructor(private db: AngularFireDatabase) {}

  private inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  private base(uid: string): string { return moneyBase(uid); }

  private listWithKeys<T>(path: string): Observable<(T & { id: string })[]> {
    return this.inCtx(() =>
      this.db.list<T>(path).snapshotChanges().pipe(
        map(snaps => snaps.map(s => ({ id: s.key!, ...(s.payload.val() as T) })))));
  }

  subscribeAll(uid: string): Observable<MoneyData> {
    const b = this.base(uid);
    return combineLatest([
      this.listWithKeys<Omit<Txn, 'id'>>(`${b}/transactions`),
      this.listWithKeys<Omit<Account, 'id'>>(`${b}/accounts`),
      this.listWithKeys<Omit<Group, 'id'>>(`${b}/groups`),
      this.listWithKeys<Omit<Category, 'id'>>(`${b}/categories`),
      this.listWithKeys<Omit<Rule, 'id'>>(`${b}/rules`),
      this.listWithKeys<Omit<ImportRecord, 'id'>>(`${b}/imports`),
      this.inCtx(() => this.db.object<number>(`${b}/meta/seededAt`).valueChanges()
        .pipe(map(v => v ?? null))),
      this.inCtx(() => this.db.object<Record<string, Budget>>(`${b}/budgets`).valueChanges()
        .pipe(map(v => v ?? {}))),
      this.inCtx(() => this.db.object<Record<string, RecurringState>>(`${b}/recurring-state`)
        .valueChanges().pipe(map(v => v ?? {}))),
      this.inCtx(() => this.db.object<BalanceIndex>(`${b}/balances`)
        .valueChanges().pipe(map(v => v ?? {}))),
    ]).pipe(map(([
      txns, accounts, groups, categories, rules, imports, seededAt, budgets,
      recurringState, balances,
    ]) => ({
      txns: txns as Txn[],
      accounts: accounts as Account[],
      groups: (groups as Group[]).sort((a, c) => a.order - c.order),
      categories: (categories as Category[]).sort((a, c) => a.order - c.order),
      rules: rules as Rule[],
      imports: imports as ImportRecord[],
      seededAt: seededAt as number | null,
      budgets: budgets as Record<string, Budget>,
      recurringState: recurringState as Record<string, RecurringState>,
      balances: balances as BalanceIndex,
    })));
  }

  /** Dedupe index. take(1) before toPromise() or the promise never resolves. */
  getFingerprints(uid: string): Promise<Set<string>> {
    return this.inCtx(() =>
      this.db.object<Record<string, string>>(`${this.base(uid)}/fingerprints`)
        .valueChanges().pipe(take(1)).toPromise())
      .then(v => new Set(Object.keys(v ?? {})));
  }

  /** Chunks are written sequentially; the import record is in the last one. */
  async commitChunks(uid: string, chunks: CommitChunk[],
                     onProgress?: (done: number, total: number) => void): Promise<void> {
    for (let i = 0; i < chunks.length; i++) {
      await this.inCtx(() => this.db.object(this.base(uid)).update(chunks[i].payload));
      onProgress?.(i + 1, chunks.length);
    }
  }

  async seedTaxonomy(uid: string): Promise<void> {
    const payload: Record<string, unknown> = {};
    SEED_GROUPS.forEach(g => {
      const { id, ...rest } = g;
      payload[`groups/${id}`] = rest;
    });
    SEED_CATEGORIES.forEach(c => {
      const { id, ...rest } = c;
      payload[`categories/${id}`] = rest;
    });
    SEED_TRANSFER_RULES.forEach(r => {
      const { id, ...rest } = r;
      payload[`rules/${id}`] = rest;
    });
    // Written in the SAME atomic update as the seed data, so the marker can
    // never be set without the data (or vice versa).
    payload['meta/seededAt'] = Date.now();
    await this.inCtx(() => this.db.object(this.base(uid)).update(payload));
  }

  upsertAccount(uid: string, account: Account): Promise<void> {
    const { id, ...rest } = account;
    return this.inCtx(() => this.db.object(`${this.base(uid)}/accounts/${id}`).update(rest));
  }

  updateTxn(uid: string, txnId: string, patch: Partial<Omit<Txn, 'id'>>): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/transactions/${txnId}`).update(patch));
  }

  /** Bulk categorize — one multi-path write rather than N round trips. */
  updateTxns(uid: string, patches: Record<string, Partial<Omit<Txn, 'id'>>>): Promise<void> {
    const payload: Record<string, unknown> = {};
    Object.entries(patches).forEach(([txnId, patch]) => {
      Object.entries(patch).forEach(([k, v]) => {
        payload[`transactions/${txnId}/${k}`] = v ?? null;   // never undefined
      });
    });
    return this.inCtx(() => this.db.object(this.base(uid)).update(payload));
  }

  addRule(uid: string, rule: Omit<Rule, 'id'>): Promise<string> {
    return this.inCtx(() => this.db.list(`${this.base(uid)}/rules`).push(rule))
      .then(ref => ref.key!);
  }

  deleteRule(uid: string, ruleId: string): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/rules/${ruleId}`).remove());
  }

  updateRule(uid: string, id: string, patch: Partial<Omit<Rule, 'id'>>): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/rules/${id}`).update(patch));
  }

  addGroup(uid: string, group: Omit<Group, 'id'>): Promise<string> {
    return this.inCtx(() => this.db.list(`${this.base(uid)}/groups`).push(group))
      .then(ref => ref.key!);
  }

  addCategory(uid: string, category: Omit<Category, 'id'>): Promise<string> {
    return this.inCtx(() => this.db.list(`${this.base(uid)}/categories`).push(category))
      .then(ref => ref.key!);
  }

  updateCategory(uid: string, id: string, patch: Partial<Omit<Category, 'id'>>): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/categories/${id}`).update(patch));
  }

  deleteCategory(uid: string, id: string): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/categories/${id}`).remove());
  }

  /** Undo: delete the transactions this import created, plus their fingerprints. */
  async deleteImport(uid: string, record: ImportRecord, txns: Txn[]): Promise<void> {
    const payload: Record<string, unknown> = {};
    const ids = new Set(record.txnIds ?? []);
    txns.filter(t => ids.has(t.id)).forEach(t => {
      payload[`transactions/${t.id}`] = null;
      payload[`fingerprints/${t.fingerprint}`] = null;
    });
    payload[`imports/${record.id}`] = null;
    await this.inCtx(() => this.db.object(this.base(uid)).update(payload));
  }

  setBudget(uid: string, categoryId: string, amount: number | null, month?: string): Promise<void> {
    return this.inCtx(() =>
      this.db.object(this.base(uid)).update(budgetPatch(categoryId, amount, month)));
  }

  setRecurringState(uid: string, seriesKey: string, status: RecurringStatus): Promise<void> {
    // seriesKey embeds a merchant stem, which is not RTDB-key-safe.
    return this.inCtx(() => this.db
      .object(`${this.base(uid)}/recurring-state/${hash64(seriesKey)}`)
      .set({ status, updatedAt: Date.now() }));
  }

  /**
   * Savings data on its own stream rather than folded into subscribeAll.
   * Only the Savings screen consumes it, and adding two more sources to the
   * shared stream would make every other screen in the feature re-render on a
   * goal edit.
   */
  subscribeSavings(uid: string): Observable<SavingsData> {
    const b = this.base(uid);
    return combineLatest([
      this.listWithKeys<Omit<SavingsGoal, 'id'>>(`${b}/goals`),
      this.inCtx(() => this.db.object<Projection>(`${b}/projection`)
        .valueChanges().pipe(map(v => v ?? DEFAULT_PROJECTION))),
    ]).pipe(map(([goals, projection]) => ({
      goals: (goals as SavingsGoal[]).sort((a, c) => a.createdAt - c.createdAt),
      projection: projection as Projection,
    })));
  }

  addGoal(uid: string, goal: Omit<SavingsGoal, 'id'>): Promise<string> {
    return this.inCtx(() => this.db.list(`${this.base(uid)}/goals`).push(goal))
      .then(ref => ref.key!);
  }

  updateGoal(uid: string, id: string, patch: Partial<Omit<SavingsGoal, 'id'>>): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/goals/${id}`).update(patch));
  }

  deleteGoal(uid: string, id: string): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/goals/${id}`).remove());
  }

  saveProjection(uid: string, projection: Projection): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/projection`).set(projection));
  }

  /**
   * Stored mortgage settings, or the defaults when the node has never been
   * written. Modelled on subscribeSavings — every read goes through inCtx so
   * AngularFire's zone-aware APIs stay inside an injection context.
   */
  subscribeMortgage(uid: string): Observable<MortgageSettings> {
    return this.inCtx(() =>
      this.db.object<MortgageSettings>(`${this.base(uid)}/mortgage`).valueChanges()
        .pipe(map(v => withDefaults(DEFAULT_MORTGAGE, v))));
  }

  saveMortgage(uid: string, settings: MortgageSettings): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/mortgage`).set(settings));
  }

  subscribeFire(uid: string): Observable<FireData> {
    return combineLatest([
      this.inCtx(() => this.db.object<FireSettings>(`${this.base(uid)}/fire`).valueChanges()
        .pipe(map(v => withDefaults(DEFAULT_FIRE, v)))),
      this.subscribeMortgage(uid),
    ]).pipe(map(([fire, mortgage]) => ({ fire, mortgage })));
  }

  saveFire(uid: string, settings: FireSettings): Promise<void> {
    return this.inCtx(() => this.db.object(`${this.base(uid)}/fire`).set(settings));
  }
}
